import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { findMatches, mapOffsets } from '@/lib/find-in-text';

// "Find in conversation": Ctrl/Cmd+F on a ticket page. Highlights every match
// in the thread, with a counter and next/previous, and keeps the browser's own
// find for when you are typing in the reply box.
//
// It never touches the thread's DOM. Messages are a mix of React-rendered
// text and sanitised email HTML, so wrapping matches in <mark> would fight
// React and break the HTML. Instead the matches are DOM Ranges painted with
// the CSS Custom Highlight API (::highlight(find-in-thread) in index.css).
// Where that API is missing, the active match is shown as a text selection.
// The matching maths lives in lib/find-in-text.ts (unit tested).

const HL_ALL = 'find-in-thread';
const HL_ACTIVE = 'find-in-thread-active';

interface HighlightRegistry {
  set(name: string, highlight: unknown): void;
  delete(name: string): void;
}

function getRegistry(): HighlightRegistry | null {
  const css = (globalThis as unknown as { CSS?: { highlights?: HighlightRegistry } }).CSS;
  return css?.highlights ?? null;
}

function makeHighlight(ranges: Range[]): unknown | null {
  const H = (globalThis as unknown as { Highlight?: new (...r: Range[]) => unknown }).Highlight;
  return H ? new H(...ranges) : null;
}

export function FindInThread({
  containerRef,
  initialQuery,
  focusMessageId,
  refreshKey,
  focusTick,
  onClose,
}: {
  /** The element holding the messages (the Thread tab's content). */
  containerRef: RefObject<HTMLElement | null>;
  initialQuery: string;
  /** From a search result: start on the first match inside this message. */
  focusMessageId?: string | null;
  /** Changes when messages arrive, so the matches are recomputed. */
  refreshKey: number;
  /** Bumped when Ctrl+F is pressed again while the bar is already open. */
  focusTick: number;
  onClose: () => void;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [ranges, setRanges] = useState<Range[]>([]);
  const [active, setActive] = useState(0);
  // Bumped only by the user's navigation (and the first jump), so a background
  // re-scan can't yank the page back to the active match while they read.
  const [jump, setJump] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const jumpedToMessage = useRef(false);

  const count = ranges.length;
  const idx = count > 0 ? Math.min(active, count - 1) : 0;

  const scan = useCallback(() => {
    const root = containerRef.current;
    const needle = query.trim();
    if (!root || !needle) {
      setRanges([]);
      return;
    }
    const nodes: Text[] = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const el = node.parentElement;
        if (!el || !node.nodeValue) return NodeFilter.FILTER_REJECT;
        // The bar itself, scripts/styles, and anything not rendered (collapsed
        // quoted history) can't be highlighted.
        if (el.closest('[data-find-skip], script, style, noscript')) return NodeFilter.FILTER_REJECT;
        if (el.getClientRects().length === 0) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    for (let n = walker.nextNode(); n; n = walker.nextNode()) nodes.push(n as Text);

    const lengths = nodes.map((n) => n.data.length);
    const full = nodes.map((n) => n.data).join('');
    const found = mapOffsets(lengths, findMatches(full, needle));
    setRanges(
      found.map((m) => {
        const r = document.createRange();
        r.setStart(nodes[m.startNode], m.startOffset);
        r.setEnd(nodes[m.endNode], m.endOffset);
        return r;
      }),
    );
  }, [containerRef, query]);

  // Re-scan on typing (debounced) and when messages arrive.
  useEffect(() => {
    const id = window.setTimeout(scan, 120);
    return () => window.clearTimeout(id);
  }, [scan, refreshKey]);

  // …and when the thread's DOM changes (images load, quoted text expands).
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    let timer: number | undefined;
    const obs = new MutationObserver(() => {
      if (timer !== undefined) return;
      timer = window.setTimeout(() => {
        timer = undefined;
        scan();
      }, 250);
    });
    obs.observe(root, { childList: true, subtree: true, characterData: true });
    return () => {
      obs.disconnect();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [containerRef, scan]);

  // Paint.
  useEffect(() => {
    const reg = getRegistry();
    if (reg) {
      const all = count > 0 ? makeHighlight(ranges) : null;
      const cur = ranges[idx] ? makeHighlight([ranges[idx]]) : null;
      if (all) reg.set(HL_ALL, all);
      else reg.delete(HL_ALL);
      if (cur) reg.set(HL_ACTIVE, cur);
      else reg.delete(HL_ACTIVE);
    } else if (ranges[idx]) {
      // No Custom Highlight API: show the active match as the selection.
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(ranges[idx]);
    }
  }, [ranges, idx, count]);

  // Clean up on close / unmount.
  useEffect(() => {
    return () => {
      const reg = getRegistry();
      reg?.delete(HL_ALL);
      reg?.delete(HL_ACTIVE);
    };
  }, []);

  // Arriving from a search result: start on the match inside that message.
  useEffect(() => {
    if (jumpedToMessage.current || count === 0) return;
    jumpedToMessage.current = true;
    if (focusMessageId) {
      const root = containerRef.current;
      const article = root?.querySelector(`[data-message-id="${CSS.escape(focusMessageId)}"]`);
      const at = article ? ranges.findIndex((r) => article.contains(r.startContainer)) : -1;
      if (at >= 0) setActive(at);
    }
    setJump((j) => j + 1);
  }, [count, ranges, focusMessageId, containerRef]);

  // Scroll to the active match when the user navigates.
  const rangesRef = useRef(ranges);
  rangesRef.current = ranges;
  const idxRef = useRef(idx);
  idxRef.current = idx;
  useEffect(() => {
    if (jump === 0) return;
    const el = rangesRef.current[idxRef.current]?.startContainer.parentElement;
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [jump]);

  const go = useCallback(
    (delta: 1 | -1) => {
      if (count === 0) return;
      setActive((i) => (Math.min(i, count - 1) + delta + count) % count);
      setJump((j) => j + 1);
    },
    [count],
  );

  // F3 / Shift+F3 while the bar is open.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'F3') return;
      e.preventDefault();
      go(e.shiftKey ? -1 : 1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  // Focus on open, and again when Ctrl+F is pressed while already open.
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [focusTick]);

  return (
    <div
      data-find-skip
      role="search"
      aria-label="Find in conversation"
      className="sticky top-[5.5rem] z-20 flex items-center gap-1.5 rounded-md border border-border/70 bg-background/95 px-2 py-1 shadow-sm backdrop-blur"
    >
      <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            go(e.shiftKey ? -1 : 1);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }
        }}
        placeholder="Find in conversation"
        aria-label="Find in conversation"
        className="h-7 min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
      />
      <span
        aria-live="polite"
        className="shrink-0 text-2xs tabular-nums text-muted-foreground"
      >
        {query.trim() === '' ? '' : count === 0 ? 'No matches' : `${idx + 1} of ${count}`}
      </span>
      <button
        type="button"
        onClick={() => go(-1)}
        disabled={count === 0}
        aria-label="Previous match"
        title="Previous match (Shift+Enter)"
        className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        disabled={count === 0}
        aria-label="Next match"
        title="Next match (Enter)"
        className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close find"
        title="Close (Esc)"
        className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
